import React, { useMemo } from 'react';
import { formatWholeNumberNaN } from '../../../../../../../core/utils/campaigns';
import EntityBreakdownCard from '../../../../../../shared/designSystem/components/EntityBreakdownCard';
import BreakdownRingChart from '../../../../../../shared/designSystem/components/BreakdownRingChart';
import DataTable from '../../../../../../shared/designSystem/components/DataTable';

// =============================================================================
// SessionDeviceBreakdown (V2) — facelifted off legacy DonutChart + AntD Card
// + AntD Table chrome to the shared `EntityBreakdownCard` (table on left,
// ring on right). Mirrors V1 SessionDeviceBreakdown.
//
// V2 input is an array of by-date breakdown rows (each carries
// {desktop, mobile, tablet} counts for a single date). Aggregate across
// all dates first, then normalize into a single rows array consumed by
// both the DataTable and the BreakdownRingChart so labels / colors stay
// in sync between the two.
// =============================================================================

const ProgressCell = ({ percent, color }) => (
  <div className="campaign-sessions-v1__progress">
    <div className="campaign-sessions-v1__progress-track">
      <div
        className="campaign-sessions-v1__progress-fill"
        style={{ width: `${Math.max(0, Math.min(100, percent))}%`, background: color }}
      />
    </div>
    <span className="campaign-sessions-v1__progress-label">
      {Number(percent).toFixed(1)}%
    </span>
  </div>
);

const DEVICE_COLORS = {
  Desktop: '#4CAF50',
  Mobile: '#2196F3',
  Tablet: '#FF9800',
};

const SessionDeviceBreakdown = ({ data = [] }) => {
  // Aggregate device counts across all by-date rows.
  const aggregated = useMemo(
    () =>
      (data || []).reduce(
        (acc, item) => ({
          desktop: acc.desktop + (item.desktop || 0),
          mobile: acc.mobile + (item.mobile || 0),
          tablet: acc.tablet + (item.tablet || 0),
        }),
        { desktop: 0, mobile: 0, tablet: 0 }
      ),
    [data]
  );

  const totalSessions = aggregated.desktop + aggregated.mobile + aggregated.tablet;

  const rows = useMemo(
    () => [
      {
        name: 'Desktop',
        value: 'Desktop',
        color: DEVICE_COLORS.Desktop,
        sessions: aggregated.desktop,
        percentage: totalSessions > 0 ? (aggregated.desktop / totalSessions) * 100 : 0,
      },
      {
        name: 'Mobile',
        value: 'Mobile',
        color: DEVICE_COLORS.Mobile,
        sessions: aggregated.mobile,
        percentage: totalSessions > 0 ? (aggregated.mobile / totalSessions) * 100 : 0,
      },
      {
        name: 'Tablet',
        value: 'Tablet',
        color: DEVICE_COLORS.Tablet,
        sessions: aggregated.tablet,
        percentage: totalSessions > 0 ? (aggregated.tablet / totalSessions) * 100 : 0,
      },
    ],
    [aggregated, totalSessions]
  );

  const columns = useMemo(
    () => [
      {
        id: 'device',
        accessorKey: 'name',
        header: 'Device',
        sortable: false,
        meta: { align: 'left', width: '32%' },
        cell: ({ row, getValue }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 9999,
                background: row.original.color,
                marginRight: 8,
                flexShrink: 0,
              }}
            />
            {getValue()}
          </span>
        ),
      },
      {
        id: 'sessions',
        accessorKey: 'sessions',
        header: 'Sessions',
        sortable: false,
        meta: { align: 'right', width: '28%' },
        cell: ({ getValue }) => formatWholeNumberNaN(getValue()),
      },
      {
        id: 'percentage',
        accessorKey: 'percentage',
        header: 'Percentage',
        sortable: false,
        meta: { align: 'left' },
        cell: ({ row, getValue }) => (
          <ProgressCell percent={Number(getValue())} color={row.original.color} />
        ),
      },
    ],
    []
  );

  return (
    <EntityBreakdownCard
      title="Device Breakdown"
      table={
        <DataTable
          data={rows}
          columns={columns}
          pageSize={3}
          pageSizeOptions={[3]}
          emptyState="No device data yet."
          className="campaign-sessions-v1__device-table"
          summaryRow={{
            labelColumnId: 'device',
            label: 'Total',
            cells: {
              sessions: formatWholeNumberNaN(totalSessions),
              percentage: '100%',
            },
          }}
        />
      }
      breakdownChart={
        <BreakdownRingChart
          data={rows}
          dimension="sessions"
          height={320}
          dimensionLabels={{ sessions: 'Sessions' }}
        />
      }
    />
  );
};

export default SessionDeviceBreakdown;
